import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope, faDownload, faFilePdf, faPalette, faClipboardList, faCheckCircle, faExclamationCircle, faUpload, faSpinner, faLock } from "@fortawesome/free-solid-svg-icons";
import "./WelcomeKit.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import { submitSupportRequest, uploadContractorFile } from "../firebase/firestore";

const WelcomeKit = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        supportType: "",
        message: "",
    });
    const [formMessage, setFormMessage] = useState({ type: "", text: "" });

    // Document upload state
    const [uploadEmail, setUploadEmail] = useState("");
    const [verifiedApplication, setVerifiedApplication] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [uploadingW9, setUploadingW9] = useState(false);
    const [uploadingDD, setUploadingDD] = useState(false);
    const [uploadMessage, setUploadMessage] = useState({ type: "", text: "" });
    const [w9File, setW9File] = useState(null);
    const [ddFile, setDdFile] = useState(null);
    const [uploadedDocs, setUploadedDocs] = useState({ w9: false, directDeposit: false });

    // Verify email to find application via Cloud Function (no direct Firestore read)
    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        setVerifying(true);
        setUploadMessage({ type: "", text: "" });

        try {
            const lookup = httpsCallable(functions, 'lookupApplicationByEmail');
            const result = await lookup({ email: uploadEmail });

            if (result.data.found) {
                const app = result.data.application;
                setVerifiedApplication(app);
                setUploadedDocs({
                    w9: app.hasW9,
                    directDeposit: app.hasDirectDeposit,
                });
                setUploadMessage({
                    type: "success",
                    text: `Found your application, ${app.name}! You can now upload your documents.`
                });
            } else {
                setUploadMessage({
                    type: "error",
                    text: "No application found with this email. Please make sure you've completed your contractor application first."
                });
            }
        } catch (error) {
            console.error("Error verifying email:", error);
            setUploadMessage({
                type: "error",
                text: "Error verifying email. Please try again."
            });
        } finally {
            setVerifying(false);
        }
    };

    // Handle file selection
    const handleFileSelect = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                setUploadMessage({ type: "error", text: "Please select a PDF file only." });
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                setUploadMessage({ type: "error", text: "File size must be less than 10MB." });
                return;
            }
            if (type === 'w9') {
                setW9File(file);
            } else {
                setDdFile(file);
            }
            setUploadMessage({ type: "", text: "" });
        }
    };

    // Upload document
    const handleUploadDocument = async (documentType) => {
        const file = documentType === 'w9' ? w9File : ddFile;
        const setUploading = documentType === 'w9' ? setUploadingW9 : setUploadingDD;

        if (!file) {
            setUploadMessage({ type: "error", text: `Please select a ${documentType === 'w9' ? 'W-9' : 'Direct Deposit'} file first.` });
            return;
        }

        setUploading(true);
        setUploadMessage({ type: "", text: "" });

        try {
            const result = await uploadContractorFile(verifiedApplication.id, documentType, file);

            const recordUpload = httpsCallable(functions, 'recordDocumentUpload');
            await recordUpload({
                applicationId: verifiedApplication.id,
                documentType,
                fileName: file.name,
                fileSize: file.size,
                storagePath: result.storagePath,
            });

            setUploadedDocs(prev => ({ ...prev, [documentType]: true }));
            if (documentType === 'w9') {
                setW9File(null);
            } else {
                setDdFile(null);
            }
            setUploadMessage({
                type: "success",
                text: `${documentType === 'w9' ? 'W-9' : 'Direct Deposit'} form uploaded successfully!`
            });
        } catch (error) {
            console.error("Error uploading document:", error);
            setUploadMessage({
                type: "error",
                text: `Error uploading ${documentType === 'w9' ? 'W-9' : 'Direct Deposit'} form: ${error.message}`
            });
        } finally {
            setUploading(false);
        }
    };

    // Reset upload form
    const handleResetUpload = () => {
        setVerifiedApplication(null);
        setUploadEmail("");
        setW9File(null);
        setDdFile(null);
        setUploadMessage({ type: "", text: "" });
        setUploadedDocs({ w9: false, directDeposit: false });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await submitSupportRequest({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                supportType: formData.supportType,
                message: formData.message,
            });
            setFormMessage({ type: "success", text: "Message sent successfully!" });
            setFormData({ name: "", email: "", phone: "", supportType: "", message: "" });
        } catch (err) {
            setFormMessage({ type: "error", text: "There was an error sending your message. Please try again." });
            console.error("Support submit error:", err);
        }
    };

    const formatPhoneNumber = (value) => {
        let phoneNumber = value.replace(/\D/g, "");
        if (phoneNumber.length >= 6) {
            phoneNumber = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        } else if (phoneNumber.length >= 3) {
            phoneNumber = phoneNumber.replace(/(\d{3})(\d{0,3})/, "($1) $2");
        }
        return phoneNumber;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setFormData((prev) => ({
            ...prev,
            phone: formatted,
        }));
    };

    const generalContacts = [
        {
            department: "Billing",
            description: "Invoices, claims, receipts",
            email: "billing@stampmedical.com",
        },
        {
            department: "Records",
            description: "Records and notes",
            email: "records@stampmedical.com",
        },
        {
            department: "Scheduling",
            description: "Appointments, compliance",
            email: "scheduling@stampmedical.com",
        },
    ];

    const directContacts = [
        {
            name: "Dr. Darren Kalanj",
            title: "Chief Medical Officer",
            email: "dr.kalanj@stampmedical.com",
        },
        {
            name: "Mayra Ventura",
            title: "Billing Manager",
            email: "mayra.ventura@stampmedical.com",
        },
        {
            name: "Marissa Dominguez",
            title: "Records Dept Manager",
            email: "marissa.dominguez@stampmedical.com",
        },
        {
            name: "Cynthia Francisco",
            title: "Contractor Liaison",
            email: "cynthia.francisco@stampmedical.com",
            phone: "480.241.0966",
        },
        {
            name: "Mayra Miranda",
            title: "Lead Scheduler",
            email: "mayra.miranda@stampmedical.com",
        },
    ];

    const documents = [
        {
            title: "Direct Deposit Form",
            description: "Set up direct deposit for your payments and reimbursements",
            fileSize: "1.2 MB",
            type: "PDF",
            downloadUrl: "docs/Direct-Deposit-Form.pdf",
        },
        {
            title: "W-9 Form",
            description: "Tax identification form required for payment processing",
            fileSize: "0.8 MB",
            type: "PDF",
            downloadUrl: "docs/W-9-Form.pdf",
        },
    ];

    const sampleInvoice = {
        title: "Sample Billing Invoice",
        description: "Example invoice format for billing submissions",
        fileSize: "0.5 MB",
        type: "XLSX",
        downloadUrl: "docs/invoice.xlsx",
    };

    const mediaKit = [
        {
            title: "Stamp Medical Logo Package",
            description: "High-resolution logos in various formats (PNG, SVG, EPS)",
            fileSize: "15.2 MB",
            type: "ZIP",
            downloadUrl: "docs/Stamp-Logo.zip",
        },
        {
            title: "Brand Guidelines",
            description: "Complete brand standards, colors, and usage guidelines",
            fileSize: "8.4 MB",
            type: "PDF",
            downloadUrl: "docs/Stamp 2025 Brand Kit.pdf",
        },
    ];

    return (
        <div className="welcome-kit">
            <div className="container">
                {/* Header Section */}
                <div className="welcome-header">
                    <h1>Welcome to Stamp Medical</h1>
                    <p className="welcome-subtitle">Your comprehensive resource center for joining our healthcare network</p>
                </div>

                {/* Documents Section */}
                <div className="section">
                    <h2>Getting Started Documents</h2>
                    <p className="section-description">Essential documents to help you get started with our network. Download and review these materials to ensure a smooth onboarding process.</p>

                    <div className="documents-grid">
                        {documents.map((doc, index) => (
                            <div
                                key={index}
                                className="document-card">
                                <div className="document-header">
                                    <div className="document-icon">
                                        <FontAwesomeIcon icon={faFilePdf} />
                                    </div>
                                    <div className="document-meta">
                                        <span className="document-type">{doc.type}</span>
                                        <span className="document-size">{doc.fileSize}</span>
                                    </div>
                                </div>
                                <div className="document-content">
                                    <h3>{doc.title}</h3>
                                    <p>{doc.description}</p>
                                </div>
                                <div className="document-actions">
                                    <a
                                        href={doc.downloadUrl}
                                        className="download-btn"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        <span className="download-icon">
                                            <FontAwesomeIcon icon={faDownload} />
                                        </span>
                                        Download
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Sample Billing Invoice */}
                    <div className="sample-invoice-card">
                        <div className="sample-invoice-header">
                            <div className="sample-invoice-icon">
                                <FontAwesomeIcon icon={faFilePdf} />
                    </div>
                            <div className="sample-invoice-meta">
                                <span className="sample-invoice-type">{sampleInvoice.type}</span>
                                <span className="sample-invoice-size">{sampleInvoice.fileSize}</span>
                                        </div>
                                    </div>
                        <div className="sample-invoice-content">
                            <h3>{sampleInvoice.title}</h3>
                            <p>{sampleInvoice.description}</p>
                        </div>
                        <div className="sample-invoice-actions">
                            <a
                                href={sampleInvoice.downloadUrl}
                                        className="download-btn"
                                        download>
                                <span className="download-icon">
                                    <FontAwesomeIcon icon={faDownload} />
                                </span>
                                        Download
                                    </a>
                                </div>
                    </div>

                    {/* Important Note */}
                    <div className="important-note">
                        <h4>
                            <FontAwesomeIcon icon={faClipboardList} />
                            Important Reminder
                        </h4>
                        <p>
                            <strong>Please note -</strong> Once you have completed these forms, upload them securely below or send them to <a href="mailto:cynthia.francisco@stampmedical.com">Cynthia Francisco</a> for processing. You can reach her at (480) 241-0966 if you have any questions.
                        </p>
                    </div>
                </div>

                {/* Secure Document Upload Section */}
                <div className="section">
                    <h2>
                        <FontAwesomeIcon icon={faLock} style={{ marginRight: '10px' }} />
                        Secure Document Upload
                    </h2>
                    <p className="section-description">
                        Upload your completed W-9 and Direct Deposit forms securely. Your documents are encrypted and stored safely.
                    </p>

                    <div className="upload-container">
                        {!verifiedApplication ? (
                            <div className="verify-email-section">
                                <div className="verify-form-container">
                                    <h3>Step 1: Verify Your Email</h3>
                                    <p>Enter the email address you used when submitting your contractor application.</p>
                                    <form onSubmit={handleVerifyEmail} className="verify-form">
                                        <div className="form-group">
                                            <input
                                                type="email"
                                                value={uploadEmail}
                                                onChange={(e) => setUploadEmail(e.target.value)}
                                                placeholder="Enter your email address"
                                                required
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={verifying}
                                        >
                                            {verifying ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                                                    Verifying...
                                                </>
                                            ) : (
                                                'Verify Email'
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        ) : (
                            <div className="upload-documents-section">
                                <div className="verified-banner">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    <span>Verified: <strong>{verifiedApplication.name}</strong> ({verifiedApplication.email})</span>
                                    <button onClick={handleResetUpload} className="change-btn">Change</button>
                                </div>

                                <div className="upload-cards-grid">
                                    {/* W-9 Upload Card */}
                                    <div className={`upload-card ${uploadedDocs.w9 ? 'uploaded' : ''}`}>
                                        <div className="upload-card-header">
                                            <div className="upload-card-icon">
                                                <FontAwesomeIcon icon={faFilePdf} />
                                            </div>
                                            <h3>W-9 Form</h3>
                                            {uploadedDocs.w9 && (
                                                <span className="uploaded-badge">
                                                    <FontAwesomeIcon icon={faCheckCircle} /> Uploaded
                                                </span>
                                            )}
                                        </div>
                                        <p>Tax identification form for payment processing</p>

                                        {!uploadedDocs.w9 ? (
                                            <div className="upload-actions">
                                                <label className="file-input-label">
                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={(e) => handleFileSelect(e, 'w9')}
                                                        className="file-input"
                                                    />
                                                    <span className="file-select-btn">
                                                        {w9File ? w9File.name : 'Select PDF File'}
                                                    </span>
                                                </label>
                                                <button
                                                    onClick={() => handleUploadDocument('w9')}
                                                    className="upload-btn"
                                                    disabled={!w9File || uploadingW9}
                                                >
                                                    {uploadingW9 ? (
                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faUpload} />
                                                            Upload
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="upload-complete">
                                                <p>Thank you for uploading your W-9 form.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Direct Deposit Upload Card */}
                                    <div className={`upload-card ${uploadedDocs.directDeposit ? 'uploaded' : ''}`}>
                                        <div className="upload-card-header">
                                            <div className="upload-card-icon">
                                                <FontAwesomeIcon icon={faFilePdf} />
                                            </div>
                                            <h3>Direct Deposit Form</h3>
                                            {uploadedDocs.directDeposit && (
                                                <span className="uploaded-badge">
                                                    <FontAwesomeIcon icon={faCheckCircle} /> Uploaded
                                                </span>
                                            )}
                                        </div>
                                        <p>Bank account information for payment deposits</p>

                                        {!uploadedDocs.directDeposit ? (
                                            <div className="upload-actions">
                                                <label className="file-input-label">
                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={(e) => handleFileSelect(e, 'directDeposit')}
                                                        className="file-input"
                                                    />
                                                    <span className="file-select-btn">
                                                        {ddFile ? ddFile.name : 'Select PDF File'}
                                                    </span>
                                                </label>
                                                <button
                                                    onClick={() => handleUploadDocument('directDeposit')}
                                                    className="upload-btn"
                                                    disabled={!ddFile || uploadingDD}
                                                >
                                                    {uploadingDD ? (
                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faUpload} />
                                                            Upload
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="upload-complete">
                                                <p>Thank you for uploading your Direct Deposit form.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Security Notice */}
                                <div className="security-notice">
                                    <FontAwesomeIcon icon={faLock} />
                                    <span>Your documents are encrypted and stored securely. Only authorized Stamp Medical staff can access them.</span>
                                </div>
                            </div>
                        )}

                        {uploadMessage.text && (
                            <div className={`upload-message ${uploadMessage.type}`}>
                                <FontAwesomeIcon icon={uploadMessage.type === 'success' ? faCheckCircle : faExclamationCircle} />
                                <span>{uploadMessage.text}</span>
                            </div>
                        )}
                    </div>
        </div>

                {/* Support Form Section */}
                <div className="section">
            <h2>Contact Support</h2>
            <p className="section-description">Need additional support? Fill out the form below and we'll route your message to the appropriate department.</p>

            <div className="support-container">
                <div className="support-form-section">
                    <div className="support-form-container">
                                {formMessage.type !== "success" && (
                            <form
                                        onSubmit={handleSubmit}
                                        className="support-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="supportName">Full Name *</label>
                                        <input
                                            type="text"
                                            id="supportName"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="supportEmail">Email Address *</label>
                                        <input
                                            type="email"
                                            id="supportEmail"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="supportPhone">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="supportPhone"
                                            name="phone"
                                            value={formData.phone}
                                                    onChange={handlePhoneChange}
                                            placeholder="(555) 123-4567"
                                                    pattern="\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}"
                                                    title="Enter a 10-digit phone number (e.g., (754) 251-5030)"
                                                    inputMode="tel"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="supportType">Type of Support *</label>
                                        <select
                                            id="supportType"
                                            name="supportType"
                                            value={formData.supportType}
                                            onChange={handleChange}
                                            required>
                                            <option value="">Select support type...</option>
                                            <option value="billing">Billing</option>
                                            <option value="records">Records</option>
                                            <option value="scheduling">Scheduling</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="supportMessage">Message *</label>
                                    <textarea
                                        id="supportMessage"
                                        name="message"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Please describe your question or issue in detail..."
                                                required
                                            />
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="submit"
                                                className="btn btn-primary">
                                                <span className="btn-icon">
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </span>
                                                Send Message
                                    </button>
                                </div>
                            </form>
                        )}

                                {formMessage.type === "success" && (
                            <div className="form-message success">
                                        <h4>
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            Message Sent Successfully!
                                        </h4>
                                <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                            </div>
                        )}

                                {formMessage.type === "error" && (
                            <div className="form-message error">
                                        <h4>
                                            <FontAwesomeIcon icon={faExclamationCircle} />
                                            Error Sending Message
                                        </h4>
                                <p>There was an error sending your message. Please try again or contact us directly.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="general-contacts-sidebar">
                    <div className="sidebar-contacts">
                                {generalContacts.map((contact, index) => (
                                    <div
                                        key={index}
                                        className="sidebar-contact-item">
                            <div className="sidebar-contact-header">
                                            <h4>{contact.department}</h4>
                                            <span className="sidebar-contact-desc">{contact.description}</span>
                            </div>
                            <a
                                            href={`mailto:${contact.email}`}
                                className="sidebar-contact-email">
                                            {contact.email}
                            </a>
                        </div>
                                ))}
                    </div>

                    <div className="sidebar-phone">
                        <a
                            href="tel:623-773-2000"
                            className="phone-link">
                            (623) 773-2000
                        </a>
                    </div>
                </div>
            </div>

            {/* Important Billing Note */}
                    <div className="important-note">
                        <h4>
                            <FontAwesomeIcon icon={faClipboardList} />
                            Important Billing Note
                        </h4>
                        <p>
                            <strong>Please note -</strong> Invoices will need to be submitted no later than six business days after the last business day of each month. Please send all invoices to <a href="mailto:billing@stampmedical.com">billing@stampmedical.com</a>
                </p>
            </div>
                </div>

                {/* Department Contacts Section */}
                <div className="section">
                    <h2>Direct Contact List</h2>
                    <p className="section-description">Need to reach a specific department? Please see the direct contact list below for all non-general questions.</p>

                    <div className="contacts-grid">
                        {directContacts.map((contact, index) => (
                            <div
                                key={index}
                                className="contact-card">
                                <div className="contact-header">
                                    <h3>{contact.name}</h3>
                                    <span className="contact-title">{contact.title}</span>
                                </div>
                                <div className="contact-info">
                                    <div className="contact-details">
                                        <div className="contact-item">
                                            <span className="contact-icon">
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </span>
                                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                        </div>
                                        {contact.phone && (
                                            <div className="contact-item">
                                                <span className="contact-icon">
                                                    <FontAwesomeIcon icon={faPhone} />
                                                </span>
                                                <a href={`tel:${contact.phone.replace(/\./g, "")}`}>{contact.phone}</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                        </div>

                {/* Media Kit Section */}
                <div className="section">
                    <h2>Media Kit</h2>
                    <p className="section-description">Professional marketing materials and brand assets to help you promote your partnership with Stamp Medical.</p>

                    <div className="media-kit-grid">
                        {mediaKit.map((item, index) => (
                            <div
                                key={index}
                                className="media-kit-card">
                                <div className="media-kit-header">
                                    <div className="media-kit-icon">
                                        <FontAwesomeIcon icon={faPalette} />
                                    </div>
                                    <div className="media-kit-meta">
                                        <span className="media-kit-type">{item.type}</span>
                                        <span className="media-kit-size">{item.fileSize}</span>
                                    </div>
                                </div>
                                <div className="media-kit-content">
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                                <div className="media-kit-actions">
                                    <a
                                        href={item.downloadUrl}
                                        className="download-btn"
                                        download>
                                        <span className="download-icon">
                                            <FontAwesomeIcon icon={faDownload} />
                                        </span>
                                        Download
                                    </a>
                                </div>
                            </div>
                        ))}
                </div>
                </div>
            </div>
        </div>
    );
};

export default WelcomeKit;
